import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Button,
  Chip,
  Stack,
} from '@mui/material';
import HubIcon from '@mui/icons-material/Hub';
import ForumIcon from '@mui/icons-material/Forum';
import BoltIcon from '@mui/icons-material/Bolt';
import PaletteIcon from '@mui/icons-material/Palette';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ApiIcon from '@mui/icons-material/Api';
import InsightsIcon from '@mui/icons-material/Insights';
import ShieldIcon from '@mui/icons-material/Shield';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudIcon from '@mui/icons-material/Cloud';
import StorageIcon from '@mui/icons-material/Storage';
import MemoryIcon from '@mui/icons-material/Memory';
import LanIcon from '@mui/icons-material/Lan';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SpeedIcon from '@mui/icons-material/Speed';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupsIcon from '@mui/icons-material/Groups';
import DnsIcon from '@mui/icons-material/Dns';
import { useLanguage } from '../contexts/LanguageContext';

type Lang = 'th' | 'en';
type Bi = { th: string; en: string };

// ── Design tokens (จาก deck rootBot) ──────────────────────────────
const NAVY = '#13204a';
const SKY = '#0ea5e9';
const INDIGO = '#6366f1';
const MUTED = '#5b6a86';
const LINE = '#e6ebf4';
const GRAD = 'linear-gradient(135deg,#0ea5e9,#6366f1)';
const NAVY_GRAD = 'linear-gradient(150deg,#0a1024 0%,#13204a 55%,#1c2a5e 100%)';
const POOL_TINT = 'linear-gradient(160deg,#eaf6fe,#f6fafe)';
const SILO_TINT = 'linear-gradient(160deg,#ecedfd,#f6f6fe)';
const FONT = "'Prompt', sans-serif";
const SOFT_SHADOW = '0 10px 30px -12px rgba(19,32,74,0.12)';

const card = {
  borderRadius: '20px',
  border: `1px solid ${LINE}`,
  backgroundColor: '#fff',
  boxShadow: SOFT_SHADOW,
  height: '100%',
};

const Glow: React.FC<{ color?: string; top?: any; right?: any; left?: any; size?: number; opacity?: number }> = ({
  color = SKY, top = '-10%', right, left, size = 520, opacity = 0.35,
}) => (
  <Box aria-hidden sx={{
    position: 'absolute', top, right, left, width: size, height: size,
    background: `radial-gradient(circle, ${color} 0%, transparent 65%)`,
    opacity, filter: 'blur(20px)', pointerEvents: 'none', zIndex: 0,
  }} />
);

const GridTexture: React.FC<{ opacity?: number }> = ({ opacity = 0.07 }) => (
  <Box aria-hidden sx={{
    position: 'absolute', inset: 0,
    backgroundImage: `radial-gradient(rgba(255,255,255,${opacity}) 1px, transparent 1px)`,
    backgroundSize: '24px 24px',
    maskImage: 'radial-gradient(ellipse at 50% 35%, #000 22%, transparent 78%)',
    WebkitMaskImage: 'radial-gradient(ellipse at 50% 35%, #000 22%, transparent 78%)',
    pointerEvents: 'none', zIndex: 0,
  }} />
);

const Reveal: React.FC<{ children: React.ReactNode; delay?: number }> = ({ children, delay = 0 }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { setSeen(true); return; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setSeen(true); io.disconnect(); } }, { threshold: 0.12, rootMargin: '0px 0px -7% 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <Box ref={ref} sx={{
      opacity: seen ? 1 : 0,
      transform: seen ? 'none' : 'translateY(26px)',
      transition: 'opacity .75s cubic-bezier(.22,1,.36,1), transform .75s cubic-bezier(.22,1,.36,1)',
      transitionDelay: `${delay}s`,
    }}>{children}</Box>
  );
};

const SpotCard: React.FC<{ children: React.ReactNode; sx?: any }> = ({ children, sx }) => {
  const ref = useRef<HTMLDivElement>(null);
  const onMove = (e: React.MouseEvent) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty('--mx', `${e.clientX - r.left}px`);
    el.style.setProperty('--my', `${e.clientY - r.top}px`);
  };
  return (
    <Card ref={ref} onMouseMove={onMove} sx={{
      ...sx, position: 'relative', overflow: 'hidden',
      transition: 'box-shadow .25s ease',
      '&::before': {
        content: '""', position: 'absolute', inset: 0, borderRadius: 'inherit', zIndex: 0,
        background: 'radial-gradient(200px circle at var(--mx,50%) var(--my,-20%), rgba(99,102,241,0.12), transparent 62%)',
        opacity: 0, transition: 'opacity .3s ease', pointerEvents: 'none',
      },
      '&:hover': { transform: 'none', boxShadow: '0 18px 42px -18px rgba(19,32,74,0.2)' },
      '&:hover::before': { opacity: 1 },
      '& > *': { position: 'relative', zIndex: 1 },
    }}>{children}</Card>
  );
};

const CountUp: React.FC<{ end: number; suffix?: string; duration?: number }> = ({ end, suffix = '', duration = 1400 }) => {
  const [val, setVal] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const done = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { setVal(end); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !done.current) {
          done.current = true;
          const start = performance.now();
          const tick = (now: number) => {
            const p = Math.min(1, (now - start) / duration);
            setVal(Math.round(end * (1 - Math.pow(1 - p, 3))));
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      });
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [end, duration]);
  return <span ref={ref}>{val}{suffix}</span>;
};

const reveal = (d: number) => ({ className: 'rb-reveal', style: { animationDelay: `${d}s` } });

const ocpbLogo = require('../assets/rootbot/ocpb-logo.png');
const pdmoLogo = require('../assets/rootbot/pdmo-logo.png');
const niaLogo = require('../assets/rootbot/nia-logo.png');
const bundhayaLogo = require('../assets/rootbot/bundhaya-logo.png');
const pokpongMascot = require('../assets/rootbot/ocpb-mascot-pokpong.png');
const payuMascot = require('../assets/rootbot/pdmo-mascot-payu.png');

// ── Bilingual content ─────────────────────────────────────────────
const T = {
  heroEyebrow: 'ENTERPRISE WHITE-LABEL AI',
  heroTitle: { th: ['แพลตฟอร์มแชตบอท AI', 'แบบ White-Label สำหรับองค์กร'], en: ['White-Label AI Chatbot', 'Platform for Enterprises'] },
  heroSub: { th: 'หนึ่งแพลตฟอร์ม รองรับได้ทุกองค์กร — แบรนด์ ช่องทาง ความรู้ และ AI ของคุณเอง แยกขาดจากกัน 100%', en: 'One platform for every organization — your own brand, channels, knowledge and AI, 100% isolated.' },
  demo: { th: 'นัดเดโม', en: 'Book a demo' },
  seeCaps: { th: 'ดูความสามารถ', en: 'See capabilities' },
  online: { th: '● ออนไลน์', en: '● Online' },
  chat1: { th: 'สวัสดีครับ มีอะไรให้ช่วยไหมครับ?', en: 'Hi! How can I help you?' },
  chat2: { th: 'เช็คสถานะคำร้องหน่อยครับ', en: 'Can you check my case status?' },
  chat3: { th: 'ได้เลยครับ ขอเลขคำร้อง...', en: 'Sure — what is your case number?' },
  trust: { th: 'ใช้งานจริงในหน่วยงานรัฐและเอกชนชั้นนำ', en: 'Trusted by leading government agencies and enterprises' },
};

const PROBLEMS: { t: Bi; d: Bi }[] = [
  { t: { th: 'ต้นทุนสูง', en: 'High cost' }, d: { th: 'จ้างทีมพัฒนา + ดูแลเซิร์ฟเวอร์เอง ต้นทุนคงที่สูงตั้งแต่ยังไม่มีผู้ใช้', en: 'Hiring a dev team and running servers means high fixed costs before you have a single user.' } },
  { t: { th: 'ปรับแต่งไม่ได้', en: 'Not customizable' }, d: { th: 'เครื่องมือสำเร็จรูปติดแบรนด์เขา ปรับบุคลิก/ช่องทาง/ความรู้ได้จำกัด', en: 'Off-the-shelf tools carry their brand and limit personality, channels and knowledge.' } },
  { t: { th: 'ข้อมูลไม่ปลอดภัย', en: 'Data is not safe' }, d: { th: 'ข้อมูลลูกค้า/PII ไปอยู่บนระบบคนอื่น ไม่ผ่านมาตรฐานหน่วยงานรัฐ', en: 'Customer data/PII sits on someone else\'s system and fails government standards.' } },
  { t: { th: 'ดูแลยาก', en: 'Hard to maintain' }, d: { th: 'แก้คำตอบทีต้อง “เทรน” ใหม่ทั้งระบบ บอตตอบทับเจ้าหน้าที่ ขยายไม่ทัน', en: 'Every fix needs a full retrain, the bot talks over staff, and it cannot scale in time.' } },
];

const KPIS: { v: Bi; l: Bi }[] = [
  { v: { th: '1', en: '1' }, l: { th: 'codebase เดียว ดูแลง่าย', en: 'single codebase, easy to maintain' } },
  { v: { th: '24/7', en: '24/7' }, l: { th: 'ตอบลูกค้าอัตโนมัติทุกช่องทาง', en: 'automated answers on every channel' } },
  { v: { th: '100%', en: '100%' }, l: { th: 'แยกข้อมูลแต่ละองค์กร', en: 'data isolation per organization' } },
  { v: { th: 'Realtime', en: 'Realtime' }, l: { th: 'เพิ่มความรู้ใช้ได้ทันที ไม่ต้องเทรน', en: 'add knowledge instantly, no retraining' } },
];

const TENANTS: { name: string; mode: 'Pool' | 'Silo'; ch: string; use: Bi; live?: boolean }[] = [
  { name: 'OCPB (สคบ.)', mode: 'Silo', ch: 'LINE + Web', use: { th: 'แจ้งเบาะแส / ตอบผู้บริโภค', en: 'Tip-offs / consumer answers' } },
  { name: 'PDMO (สบน.)', mode: 'Silo', ch: 'Web', use: { th: 'หนี้สาธารณะ + สถิติเรียลไทม์', en: 'Public debt + real-time stats' } },
  { name: 'NIA (สนช.)', mode: 'Pool', ch: 'Web', use: { th: 'ถาม-ตอบ / สรุปเอกสาร MOU', en: 'Q&A / MOU document summaries' } },
  { name: 'Bundhaya Speedboat', mode: 'Pool', ch: 'Web · LINE', use: { th: 'จองเรือ / เช็คโปร', en: 'Boat booking / promotions' }, live: true },
];

const CAPS: { icon: React.ReactNode; t: Bi; d: Bi }[] = [
  { icon: <HubIcon />, t: { th: 'ทุกช่องทางในที่เดียว', en: 'Every channel, one place' }, d: { th: 'Web Widget · แอปมือถือ · LINE OA · Facebook · API — ทักช่องทางไหนก็คุยบอตเดียวกัน', en: 'Web Widget · mobile · LINE OA · Facebook · API — same bot on any channel.' } },
  { icon: <PsychologyIcon />, t: { th: 'AI Agent ที่ฉลาดจริง', en: 'A genuinely smart AI agent' }, d: { th: 'เลือก Gemini หรือ Local LLM · ค้นความรู้ (RAG) ก่อนตอบ ไม่มั่ว · เรียกเครื่องมือจริง', en: 'Gemini or your Local LLM · retrieves knowledge (RAG) before answering · calls real tools.' } },
  { icon: <BoltIcon />, t: { th: 'ความรู้แบบ Real-time', en: 'Real-time knowledge' }, d: { th: 'เพิ่ม/แก้ Q&A หรือเอกสาร แล้วบอตใช้ได้ทันที ไม่ต้องกดเทรน', en: 'Add/edit Q&A or documents and the bot uses them instantly — no retraining.' } },
  { icon: <PaletteIcon />, t: { th: 'White-label เต็มรูปแบบ', en: 'Full white-label' }, d: { th: 'โลโก้ สี ฟอนต์ ชื่อบอท บุคลิก โดเมน — ทุกอย่างเป็นแบรนด์คุณ ปรับจากหน้าจอ', en: 'Logo, colors, fonts, bot name, persona, domain — all your brand, set from the dashboard.' } },
  { icon: <SupportAgentIcon />, t: { th: 'Live Chat ทุกช่องทาง', en: 'Live chat on every channel' }, d: { th: 'ส่งต่อให้เจ้าหน้าที่คุยสด บอตหยุดอัตโนมัติเมื่อคนเข้ามา ไม่ตอบทับ', en: 'Hand off to a live agent; the bot pauses automatically and never talks over them.' } },
  { icon: <MemoryIcon />, t: { th: 'ความจำระยะยาว', en: 'Long-term memory' }, d: { th: 'จำลูกค้าได้ข้ามบทสนทนา — ชื่อ ความชอบ ประวัติ ไม่ต้องถามซ้ำ', en: 'Remembers customers across chats — name, preferences, history — no repeat questions.' } },
  { icon: <ApiIcon />, t: { th: 'เชื่อมระบบภายนอก', en: 'Connect external systems' }, d: { th: 'ต่อ API จริง — เช็คสต็อก จองคิว ติดตามสถานะ ออกลิงก์ชำระเงิน (REST/N8N/MCP)', en: 'Real API calls — check stock, book slots, track status, issue payment links (REST/N8N/MCP).' } },
  { icon: <InsightsIcon />, t: { th: 'รายงาน + วิเคราะห์', en: 'Reports + analytics' }, d: { th: 'แดชบอร์ดเรียลไทม์ — ปริมาณแชต อัตราตอบได้ คำถามที่ตอบไม่ได้ คะแนนรีวิว', en: 'Real-time dashboard — chat volume, answer rate, unanswered questions, review scores.' } },
  { icon: <ShieldIcon />, t: { th: 'ปลอดภัยระดับองค์กร', en: 'Enterprise-grade security' }, d: { th: 'Row-Level Security · สิทธิ์ฝั่งเซิร์ฟเวอร์ · เข้ารหัส credential · ผู้ดูแลแอบดูไม่ได้', en: 'Row-Level Security · server-side authorization · encrypted credentials · no silent admin access.' } },
];

const HOW: { t: Bi; d: Bi }[] = [
  { t: { th: 'ค้นความรู้ก่อนตอบ — ไม่มั่ว', en: 'Retrieves before answering — no hallucinations' }, d: { th: 'บอตค้นความรู้ (RAG) ก่อนตอบเสมอ ตอบจากข้อมูลจริง อ้างอิงเอกสาร ไม่แต่งเอง — หรือเรียก Tool/API จริง เช่น เช็คคิว จอง ชำระเงิน', en: 'The bot always retrieves knowledge (RAG) first and answers from real, document-grounded data — or calls real tools/APIs such as queue checks, bookings and payments.' } },
  { t: { th: 'เพิ่มความรู้แล้วใช้ได้ทันที', en: 'New knowledge works instantly' }, d: { th: 'เพิ่ม/แก้ Q&A หรือเอกสาร → embed ทันที → เก็บใน pgvector ในฐานข้อมูลเดียวกัน → บอตใช้ได้เลย ไม่ต้องกดเทรนหรือรอ sync', en: 'Add/edit Q&A or docs → embedded instantly → stored in pgvector in the same database → the bot uses it right away, no retraining or sync wait.' } },
  { t: { th: 'เจ้าหน้าที่เข้ามา บอตหยุดทันที', en: 'When an agent joins, the bot stops' }, d: { th: 'ทุกบทสนทนามีสถานะกลาง บอตหยุดอัตโนมัติเมื่อเจ้าหน้าที่เข้ามาคุย ไม่ตอบทับกัน ทุกขั้นถูกบันทึก', en: 'Every conversation has a shared state; the bot pauses automatically when staff step in, never overlapping, with every step logged.' } },
];

const POOL = {
  bullets: [
    [{ th: 'ต้นทุนต่ำสุด', en: 'Lowest cost' }, { th: 'เพิ่มลูกค้าใหม่แทบไม่มีต้นทุนเพิ่ม ใช้เซิร์ฟเวอร์/ฐานข้อมูลร่วม', en: 'Adding a new tenant costs almost nothing — shared servers/database.' }],
    [{ th: 'เปิดใช้เร็ว', en: 'Fast to launch' }, { th: 'สร้างองค์กรใหม่ได้ในไม่กี่นาที ไม่ต้อง deploy', en: 'Spin up a new organization in minutes — no deploy.' }],
    [{ th: 'ปรับแต่งผ่านการตั้งค่า', en: 'Configure, don\'t code' }, { th: 'แบรนด์ ช่องทาง ความรู้ ฟีเจอร์', en: 'Brand, channels, knowledge, features.' }],
    [{ th: 'Scale อัตโนมัติ', en: 'Auto-scaling' }, { th: 'แยก index ความรู้รายเจ้า อัปเดตพร้อมกันทุกองค์กร', en: 'Per-tenant knowledge index; features ship to everyone at once.' }],
  ] as [Bi, Bi][],
  fit: { th: 'SME · เอกชน · ลูกค้าจำนวนมาก ที่อยากได้บอตคุณภาพ เริ่มเร็ว', en: 'SMEs · private firms · high-volume clients who want a quality bot, fast.' },
};
const SILO = {
  bullets: [
    [{ th: 'แยกขาดสูงสุด', en: 'Maximum isolation' }, { th: 'ฐานข้อมูล/โครงสร้างแยกเครื่อง เหมาะ compliance รัฐ', en: 'Separate database/infrastructure — ideal for government compliance.' }],
    [{ th: 'โดเมนของตัวเอง', en: 'Your own domain' }, { th: 'ปรับ UI/logic/backend ได้อิสระเต็มที่', en: 'Fully customize UI/logic/backend.' }],
    [{ th: 'เลือก Data Residency', en: 'Choose data residency' }, { th: 'กำหนดโซนเซิร์ฟเวอร์เองได้', en: 'Pick your server region.' }],
    [{ th: 'ต่อระบบภายในได้ลึก', en: 'Deep internal integration' }, { th: 'N8N · ระบบเดิม — ใช้ component กลาง อัปเดตได้ ไม่ต้อง rewrite', en: 'N8N · legacy systems — shared versioned components, upgradable without rewrites.' }],
  ] as [Bi, Bi][],
  fit: { th: 'หน่วยงานรัฐ · องค์กรใหญ่ ที่ต้องการ isolation และผ่าน PII/compliance', en: 'Government · large enterprises needing isolation and PII/compliance.' },
};

const COMPARE: [Bi, Bi, Bi][] = [
  [{ th: 'ต้นทุนต่อองค์กร', en: 'Cost per org' }, { th: 'ต่ำสุด — แชร์ทรัพยากร', en: 'Lowest — shared resources' }, { th: 'สูงกว่า — เซิร์ฟเวอร์/DB แยก', en: 'Higher — separate server/DB' }],
  [{ th: 'ความเร็วในการเปิดใช้', en: 'Time to launch' }, { th: 'เป็นนาที — ไม่ต้อง deploy', en: 'Minutes — no deploy' }, { th: 'ตั้งระบบแยกครั้งแรก', en: 'One-time setup' }],
  [{ th: 'การแยกข้อมูล', en: 'Data isolation' }, { th: 'Row-Level Security (cross-tenant = 0)', en: 'Row-Level Security (cross-tenant = 0)' }, { th: 'ฐานข้อมูลแยกเครื่อง — แยกขาดกายภาพ', en: 'Separate DB — physical isolation' }],
  [{ th: 'การปรับแต่ง', en: 'Customization' }, { th: 'ตั้งค่า + เลือกจาก catalog', en: 'Config + catalog' }, { th: 'เต็มที่ทุกบรรทัด (own app)', en: 'Unlimited (own app)' }],
  [{ th: 'โดเมน', en: 'Domain' }, { th: 'โดเมนย่อย / ของแพลตฟอร์ม', en: 'Subdomain / platform' }, { th: 'โดเมนของคุณเอง', en: 'Your own domain' }],
  [{ th: 'เหมาะกับ', en: 'Best for' }, { th: 'เอกชน · SME · ลูกค้าจำนวนมาก', en: 'Private · SME · high volume' }, { th: 'รัฐ · องค์กรใหญ่ · compliance สูง', en: 'Gov · large · high compliance' }],
];

const LAYERS: { icon: React.ReactNode; t: Bi; d: Bi }[] = [
  { icon: <HubIcon />, t: { th: 'ช่องทาง', en: 'Channels' }, d: { th: 'Web · LINE · Facebook · API', en: 'Web · LINE · Facebook · API' } },
  { icon: <CloudIcon />, t: { th: 'แกนประมวลผล', en: 'Compute core' }, d: { th: 'Cloud Run — real-time, auto-scale', en: 'Cloud Run — real-time, auto-scale' } },
  { icon: <PsychologyIcon />, t: { th: 'AI', en: 'AI' }, d: { th: 'Gemini + เวกเตอร์ค้นความรู้ (RAG)', en: 'Gemini + vector retrieval (RAG)' } },
  { icon: <StorageIcon />, t: { th: 'ข้อมูล', en: 'Data' }, d: { th: 'PostgreSQL · BigQuery · Cloud Storage', en: 'PostgreSQL · BigQuery · Cloud Storage' } },
];

const SECURITY: Bi[] = [
  { th: 'แยกข้อมูลด้วย Row-Level Security', en: 'Data isolation with Row-Level Security' },
  { th: 'สิทธิ์ตรวจฝั่งเซิร์ฟเวอร์ (RBAC default-deny)', en: 'Server-side authorization (RBAC default-deny)' },
  { th: 'เข้ารหัส credential ใน Secret Manager', en: 'Credentials encrypted in Secret Manager' },
  { th: 'ผู้ดูแลแอบดูข้อมูลไม่ได้ (audited)', en: 'No silent admin access (audited)' },
  { th: 'cross-tenant leak = 0 (พิสูจน์ด้วย integration test)', en: 'cross-tenant leak = 0 (proven by integration tests)' },
  { th: 'คุมต้นทุน AI ต่อ tenant (quota + เพดาน)', en: 'Per-tenant AI cost control (quota + caps)' },
];

const STEPS: { t: Bi; d: Bi }[] = [
  { t: { th: 'สร้างองค์กร', en: 'Create the org' }, d: { th: 'ตั้งชื่อ เลือก Pool/Silo กำหนดแบรนด์ สี โลโก้ บุคลิกบอท', en: 'Name it, pick Pool/Silo, set brand, colors, logo and bot persona.' } },
  { t: { th: 'ใส่ความรู้', en: 'Add knowledge' }, d: { th: 'อัปโหลด Q&A + เอกสาร → บอตเรียนรู้ทันที เชื่อม API ที่ต้องการ', en: 'Upload Q&A + documents → the bot learns instantly; connect any APIs.' } },
  { t: { th: 'เปิดช่องทาง', en: 'Open channels' }, d: { th: 'ผูก Web, LINE OA, Facebook ด้วยข้อมูลขององค์กรเอง', en: 'Connect Web, LINE OA and Facebook with the org\'s own accounts.' } },
  { t: { th: 'เปิดใช้งาน', en: 'Go live' }, d: { th: 'วางโค้ดฝังเว็บ 1 บรรทัด → พร้อมตอบลูกค้า 24/7 ทันที', en: 'Drop in one line of embed code → answering customers 24/7 instantly.' } },
];

const ROI: { icon: React.ReactNode; t: Bi; d: Bi }[] = [
  { icon: <AccessTimeIcon />, t: { th: 'ตอบลูกค้า 24/7', en: 'Answer 24/7' }, d: { th: 'ลดภาระเจ้าหน้าที่อย่างมีนัยสำคัญ', en: 'Significantly reduces staff workload.' } },
  { icon: <SpeedIcon />, t: { th: 'ตอบเร็วขึ้น', en: 'Faster responses' }, d: { th: 'ปิดการขาย / ให้บริการได้มากขึ้น', en: 'Close more sales / serve more people.' } },
  { icon: <TrendingUpIcon />, t: { th: 'โตได้ไม่ต้องจ้างเพิ่ม', en: 'Grow without hiring' }, d: { th: 'ขยายบริการโดยไม่ต้องเพิ่มทีมซัพพอร์ต', en: 'Scale service without growing the support team.' } },
  { icon: <InsightsIcon />, t: { th: 'ได้ insight ลูกค้า', en: 'Customer insight' }, d: { th: 'เก็บข้อมูลเชิงลึกจากทุกบทสนทนา', en: 'Capture deep insight from every conversation.' } },
];

const CUSTOMERS: { logo: string; name: string; sub: Bi; mode: 'Pool' | 'Silo'; accent: string; live?: boolean; darkTile?: boolean; mascot?: string; mascotName?: Bi; points: Bi[] }[] = [
  {
    logo: ocpbLogo, name: 'OCPB (สคบ.)', sub: { th: 'สำนักงานคณะกรรมการคุ้มครองผู้บริโภค', en: 'Office of the Consumer Protection Board' }, mode: 'Silo', accent: INDIGO, mascot: pokpongMascot, mascotName: { th: 'พี่ปกป้อง', en: 'Pokpong' },
    points: [
      { th: 'ตอบคำถามผู้บริโภค (RAG) — สิทธิ ขั้นตอน FAQ', en: 'Answers consumer questions (RAG) — rights, steps, FAQs' },
      { th: 'แจ้งเบาะแส/ร้องเรียน — ฟอร์มหลายขั้น + แนบหลักฐาน + รหัสติดตาม', en: 'Tip-offs/complaints — multi-step forms + evidence + tracking code' },
      { th: 'ตรวจทะเบียนนิติบุคคล/อย./ขายตรง', en: 'Registry checks — business / FDA / direct-sales' },
      { th: 'Live chat + log ครบทุกช่องทาง', en: 'Live chat + logs across every channel' },
    ],
  },
  {
    logo: pdmoLogo, name: 'PDMO (สบน.)', sub: { th: 'สำนักงานบริหารหนี้สาธารณะ', en: 'Public Debt Management Office' }, mode: 'Silo', accent: INDIGO, mascot: payuMascot, mascotName: { th: 'พี่วายุ', en: 'Payu' },
    points: [
      { th: 'ถาม-ตอบหนี้สาธารณะ + นโยบาย — RAG จากเอกสาร/พ.ร.ก.', en: 'Public-debt & policy Q&A — RAG over documents/decrees' },
      { th: 'ดึงสถิติหนี้เรียลไทม์จาก API — หนี้รวม/สกุลเงิน/ต่อ GDP', en: 'Real-time debt stats via API — total/currency/% of GDP' },
      { th: 'Live chat + รีวิว 5 ดาว', en: 'Live chat + 5-star reviews' },
    ],
  },
  {
    logo: niaLogo, name: 'NIA (สนช.)', sub: { th: 'สำนักงานนวัตกรรมแห่งชาติ', en: 'National Innovation Agency' }, mode: 'Pool', accent: SKY, darkTile: true,
    points: [
      { th: 'ถาม-ตอบฐาน MOU/MOA ~492 ฉบับ', en: 'Q&A over ~492 MOU/MOA records' },
      { th: 'ค้นคู่ความร่วมมือ ตามประเทศ/ภาคส่วน/สถานะ', en: 'Find partners by country/sector/status' },
      { th: 'สรุปเอกสาร PDF อัตโนมัติ', en: 'Automatic PDF document summaries' },
      { th: 'Public dashboard — แผนที่ + กราฟความร่วมมือ', en: 'Public dashboard — map + partnership charts' },
    ],
  },
  {
    logo: bundhayaLogo, name: 'Bundhaya Speedboat', sub: { th: 'บอตจองเรือ omnichannel บน production', en: 'Omnichannel boat-booking bot in production' }, mode: 'Pool', accent: SKY, live: true,
    points: [
      { th: 'ตอบเส้นทาง/ตาราง/ราคา/ที่นั่งว่าง เรียลไทม์', en: 'Real-time routes/schedules/prices/seat availability' },
      { th: 'เช็คโปรจริงผ่าน API (capability-gated)', en: 'Live promotions via a capability-gated API' },
      { th: 'signed-login รู้จักสมาชิก — โปรเฉพาะสมาชิก', en: 'signed-login recognizes members — member-only offers' },
      { th: 'ส่งลิงก์จอง → ปิดการขายในแชต', en: 'Sends booking links → closes the sale in chat' },
    ],
  },
];

const WHY: Bi[] = [
  { th: 'White-label จริง — เป็นแบรนด์คุณ 100% ไม่ใช่ติดแบรนด์คนอื่น', en: 'True white-label — 100% your brand, not someone else\'s' },
  { th: 'รองรับทั้ง Pool และ Silo — เล็กก็เริ่มได้ ใหญ่/รัฐก็แยกเดี่ยวได้ ในแพลตฟอร์มเดียว', en: 'Both Pool and Silo — start small or run isolated for gov/large, one platform' },
  { th: 'ฉลาดจริง + real-time — RAG + tools + ไม่ต้องเทรน', en: 'Genuinely smart + real-time — RAG + tools + no retraining' },
  { th: 'ปลอดภัยระดับรัฐ — isolation + audited + compliance-ready', en: 'Government-grade security — isolation + audited + compliance-ready' },
  { th: 'พิสูจน์แล้วใน production — สร้างบน Google Cloud', en: 'Proven in production — built on Google Cloud' },
  { th: 'Widget ผูกตัวตนได้ — signed-login (HMAC) + ฝังได้ทุก security policy', en: 'Identity-aware widget — signed-login (HMAC) + embeds under any security policy' },
];

const STATS: { n?: number; suffix?: string; text?: string; label: Bi }[] = [
  { n: 4, label: { th: 'องค์กรใช้งานจริง (รัฐ + เอกชน)', en: 'organizations in production (gov + private)' } },
  { n: 492, suffix: '+', label: { th: 'เอกสาร MOU จัดการด้วย AI (NIA)', en: 'MOU documents handled by AI (NIA)' } },
  { n: 0, label: { th: 'cross-tenant leak (ทดสอบจริง)', en: 'cross-tenant leaks (tested)' } },
  { text: '24/7', label: { th: 'ตอบลูกค้าทุกช่องทาง', en: 'answering on every channel' } },
];

// section eyebrows / titles / leads
const SEC = {
  problemEye: { th: 'ปัญหา · The Problem', en: 'The Problem' },
  problemTitle: { th: 'อยากมีแชตบอท แต่ทุกทางเลือกมีกำแพง', en: 'You want a chatbot — but every option hits a wall' },
  solutionEye: { th: 'โซลูชัน · The Solution', en: 'The Solution' },
  solutionTitle: { th: 'rootBot — แพลตฟอร์มเดียว เปิดบริการให้ได้ทุกองค์กร', en: 'rootBot — one platform to serve every organization' },
  solutionLead: { th: 'แพลตฟอร์มแชตบอท multi-tenant แบบ white-label เพิ่มลูกค้า/หน่วยงานใหม่ได้ในแพลตฟอร์มเดียว โดยไม่ต้องเขียนโค้ดใหม่ — แต่ละองค์กรมีแบรนด์ ความรู้ และ AI ของตัวเอง', en: 'A multi-tenant, white-label chatbot platform — add new clients/agencies on one platform with no new code; each org has its own brand, knowledge and AI.' },
  crossTenant: { th: 'cross-tenant leak = 0 · ปลอดภัยระดับหน่วยงานรัฐ', en: 'cross-tenant leak = 0 · government-grade security' },
  platformEye: { th: 'ภาพรวม · Architecture', en: 'Overview · Architecture' },
  platformTitle: { th: 'แพลตฟอร์มเดียว → หลายองค์กร', en: 'One platform → many organizations' },
  platformNodeSub: { th: 'tenant · AI · ช่องทาง · ความรู้ · ความปลอดภัย', en: 'tenant · AI · channels · knowledge · security' },
  capsEye: { th: 'ความสามารถหลัก · Features', en: 'Core Capabilities · Features' },
  capsTitle: { th: 'ครบทุกอย่างที่แชตบอทองค์กรต้องมี', en: 'Everything an enterprise chatbot needs' },
  howEye: { th: 'การทำงานของ AI · How it works', en: 'How the AI works' },
  howTitle: { th: 'ฉลาด แม่นยำ และไม่ตอบมั่ว', en: 'Smart, accurate, never makes things up' },
  deployEye: { th: 'รูปแบบการติดตั้ง · Deployment', en: 'Deployment Models' },
  deployTitle: { th: 'เลือกได้ — Pool ใช้ร่วม หรือ Silo แยกเดี่ยว', en: 'Your choice — shared Pool or isolated Silo' },
  deployLead: { th: 'สองรูปแบบบนแพลตฟอร์มเดียวกัน ใช้โค้ดชุดเดียว — เลือกตามขนาด งบประมาณ และข้อกำหนดด้านความปลอดภัย', en: 'Two models on one platform from a single codebase — choose by size, budget and security requirements.' },
  poolTag: { th: 'ใช้ทรัพยากรร่วม', en: 'shared resources' },
  siloTag: { th: 'แยกเดี่ยวเต็มตัว', en: 'fully isolated' },
  fitLabel: { th: 'เหมาะกับ · ', en: 'Best for · ' },
  migrate: { th: 'ย้ายขึ้นได้ด้วยการปรับ config — ไม่ต้องแก้โค้ด', en: 'Move up by changing config — no code changes' },
  roiEye: { th: 'คุณค่าทางธุรกิจ · ROI', en: 'Business Value · ROI' },
  roiTitle: { th: 'คืนทุนจากการลดงานซ้ำของทีมซัพพอร์ต', en: 'ROI from cutting repetitive support work' },
  archEye: { th: 'สถาปัตยกรรม · Security', en: 'Architecture · Security' },
  archTitle: { th: 'สร้างบน Google Cloud — มั่นคง ปลอดภัย ขยายได้', en: 'Built on Google Cloud — reliable, secure, scalable' },
  layerWord: { th: 'ชั้นที่', en: 'Layer' },
  securityTitle: { th: 'ความปลอดภัยในตัว', en: 'Built-in security' },
  modelTitle: { th: 'เลือกสมองของบอตได้', en: 'Choose the bot\'s brain' },
  frontierDesc: { th: 'Google Gemini และโมเดลชั้นนำ — ฉลาดสูงสุด อัปเดตล่าสุดเสมอ', en: 'Google Gemini and frontier models — highest intelligence, always up to date.' },
  localDesc: { th: 'รันบนเครื่ององค์กรเอง — ข้อมูลไม่ออกนอกองค์กร เหมาะ PII / compliance หน่วยงานรัฐ', en: 'Runs on your own machines — data never leaves the org; ideal for PII / government compliance.' },
  onboardEye: { th: 'เริ่มต้นใช้งาน · Onboarding', en: 'Getting Started · Onboarding' },
  onboardTitle: { th: 'เปิดบริการองค์กรใหม่ — ไม่ต้องเขียนโค้ด', en: 'Launch a new organization — no code required' },
  custEye: { th: 'ลูกค้าจริง · Our Customers', en: 'Our Customers' },
  custTitle: { th: 'พิสูจน์แล้วในหน่วยงานรัฐและเอกชน', en: 'Proven in government and enterprise' },
  botLabel: { th: 'บอท', en: 'Bot' },
  whyEye: { th: 'สรุปจุดแข็ง · Why rootBot', en: 'Why rootBot' },
  whyTitle: { th: 'จุดแข็งที่ต่างจากทางเลือกอื่น', en: 'What sets rootBot apart' },
  ctaTitle: { th: ['พร้อมให้องค์กรของคุณ', 'มีแชตบอท AI เป็นของตัวเอง'], en: ['Give your organization', 'its own AI chatbot'] },
  ctaSub: { th: 'นัดเดโมดูระบบจริง — ทั้ง Pool และ Silo · ประเมินแผนการใช้งานสำหรับองค์กรของคุณ', en: 'Book a demo of the real system — both Pool and Silo · we\'ll assess a plan for your organization.' },
  ctaNote: { th: 'เลือกได้ตามขนาดองค์กร — เริ่ม Pool แล้วขยายเป็น Silo ในแพลตฟอร์มเดียว', en: 'Scales to your size — start on Pool and grow into Silo on the same platform.' },
  ctaBtn: { th: 'ติดต่อ / นัดเดโม', en: 'Contact / Book a demo' },
  stickyMsg: { th: 'พร้อมให้องค์กรของคุณมีแชตบอท AI เป็นของตัวเอง', en: 'Give your organization its own AI chatbot' },
};

// ── small presentational helpers ──────────────────────────────────
const Eyebrow: React.FC<{ children: React.ReactNode; light?: boolean }> = ({ children, light }) => (
  <Stack direction="row" alignItems="center" spacing={1} justifyContent="center" sx={{ mb: 2 }}>
    <Box sx={{ width: 26, height: 2, borderRadius: 2, background: light ? 'rgba(125,211,252,0.8)' : GRAD }} />
    <Typography sx={{ fontWeight: 600, fontSize: '0.78rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: light ? '#7dd3fc' : INDIGO }}>{children}</Typography>
  </Stack>
);
const SectionTitle: React.FC<{ children: React.ReactNode; light?: boolean }> = ({ children, light }) => (
  <Typography component="h2" sx={{ fontWeight: 700, fontSize: { xs: '1.9rem', md: '2.6rem' }, lineHeight: 1.18, color: light ? '#fff' : NAVY, textWrap: 'balance', letterSpacing: '-0.01em' }}>{children}</Typography>
);
const Lead: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ color: MUTED, maxWidth: 720, mx: 'auto', mt: 2, fontSize: { xs: '0.98rem', md: '1.08rem' }, lineHeight: 1.7, textWrap: 'pretty' }}>{children}</Typography>
);
const Section: React.FC<{ children: React.ReactNode; bg?: string; sx?: object }> = ({ children, bg, sx }) => (
  <Box sx={{ py: { xs: 8, md: 13 }, background: bg || '#fff', position: 'relative', ...sx }}>
    <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}><Reveal>{children}</Reveal></Container>
  </Box>
);

const RootBotPage: React.FC = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const L = language as Lang;
  const tr = (o: Bi) => o[L];
  const [showBar, setShowBar] = useState(false);
  useEffect(() => { window.scrollTo({ top: 0 }); }, []);
  useEffect(() => {
    const onScroll = () => setShowBar(window.scrollY > 720);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const goDemo = () => {
    navigate('/');
    setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }), 120);
  };

  const TRUST = [
    { logo: ocpbLogo, name: 'OCPB', dark: false },
    { logo: pdmoLogo, name: 'PDMO', dark: false },
    { logo: niaLogo, name: 'NIA', dark: true },
    { logo: bundhayaLogo, name: 'Bundhaya Speedboat', dark: false },
  ];

  return (
    <Box sx={{ fontFamily: FONT, '& *': { fontFamily: FONT }, color: NAVY, '& .MuiCard-root:hover': { transform: 'none' } }}>
      {/* ── 1. HERO ── */}
      <Box sx={{ position: 'relative', background: NAVY_GRAD, color: '#fff', overflow: 'hidden', pt: { xs: 13, md: 17 }, pb: { xs: 9, md: 14 } }}>
        <Glow color={SKY} top="-18%" right="-8%" size={620} opacity={0.5} />
        <Glow color={INDIGO} top="40%" left="-12%" size={520} opacity={0.32} />
        <GridTexture opacity={0.08} />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 7 }}>
              <Stack {...reveal(0)} direction="row" alignItems="center" spacing={1.2} sx={{ mb: 3 }}>
                <Box sx={{ width: 34, height: 34, borderRadius: '9px', background: GRAD, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ForumIcon sx={{ color: '#fff', fontSize: 19 }} />
                </Box>
                <Typography sx={{ fontWeight: 600, fontSize: '1.2rem' }}>rootBot</Typography>
              </Stack>
              <Stack {...reveal(0.08)} direction="row" alignItems="center" spacing={1} sx={{ mb: 2.5 }}>
                <Box sx={{ width: 26, height: 2, background: 'rgba(125,211,252,0.9)', borderRadius: 2 }} />
                <Typography sx={{ fontWeight: 600, fontSize: '0.78rem', letterSpacing: '0.2em', color: '#7dd3fc' }}>{T.heroEyebrow}</Typography>
              </Stack>
              <Typography component="h1" {...reveal(0.16)} sx={{ fontWeight: 700, fontSize: { xs: '2.4rem', md: '3.6rem' }, lineHeight: 1.12, letterSpacing: '-0.02em', textWrap: 'balance', mb: 2.5 }}>
                {T.heroTitle[L][0]}<br />{T.heroTitle[L][1]}
              </Typography>
              <Typography {...reveal(0.24)} sx={{ fontSize: { xs: '1.05rem', md: '1.22rem' }, color: 'rgba(255,255,255,0.78)', lineHeight: 1.7, maxWidth: 560, textWrap: 'pretty', mb: 4 }}>
                {tr(T.heroSub)}
              </Typography>
              <Stack {...reveal(0.32)} direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button className="rb-shine" onClick={goDemo} size="large" endIcon={<ArrowForwardIcon />} sx={{ background: GRAD, color: '#fff', px: 4, py: 1.6, fontWeight: 600, borderRadius: '12px', boxShadow: '0 12px 28px -10px rgba(14,165,233,0.6)', '&:hover': { opacity: 0.93 } }}>
                  {tr(T.demo)}
                </Button>
                <Button href="#capabilities" size="large" sx={{ color: '#fff', px: 4, py: 1.6, fontWeight: 500, borderRadius: '12px', border: '1px solid rgba(255,255,255,0.28)', '&:hover': { background: 'rgba(255,255,255,0.06)' } }}>
                  {tr(T.seeCaps)}
                </Button>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }} sx={{ position: 'relative' }}>
              <Box className="rb-float" sx={{ display: { xs: 'none', md: 'flex' }, position: 'absolute', top: -12, left: -6, zIndex: 3, alignItems: 'center', gap: 0.8, px: 1.6, py: 0.8, borderRadius: '999px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)', backdropFilter: 'blur(8px)' }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#06C755' }} />
                <Typography sx={{ color: '#fff', fontSize: '0.74rem', fontWeight: 600 }}>LINE OA</Typography>
              </Box>
              <Box className="rb-float-2" sx={{ display: { xs: 'none', md: 'flex' }, position: 'absolute', bottom: 18, left: -22, zIndex: 3, alignItems: 'center', gap: 0.8, px: 1.6, py: 0.8, borderRadius: '999px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)', backdropFilter: 'blur(8px)' }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#1877F2' }} />
                <Typography sx={{ color: '#fff', fontSize: '0.74rem', fontWeight: 600 }}>Facebook</Typography>
              </Box>
              <Box className="rb-float-3" sx={{ display: { xs: 'none', md: 'flex' }, position: 'absolute', top: 44, right: -14, zIndex: 3, alignItems: 'center', gap: 0.8, px: 1.6, py: 0.8, borderRadius: '999px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)', backdropFilter: 'blur(8px)' }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: SKY }} />
                <Typography sx={{ color: '#fff', fontSize: '0.74rem', fontWeight: 600 }}>Web Widget</Typography>
              </Box>

              <Card {...reveal(0.4)} sx={{ position: 'relative', borderRadius: '22px', p: 2.5, background: 'linear-gradient(180deg,rgba(255,255,255,0.13),rgba(255,255,255,0.05))', border: '1px solid rgba(255,255,255,0.16)', backdropFilter: 'blur(14px)', boxShadow: '0 30px 70px -20px rgba(0,0,0,0.6)', '&::before': { content: '""', position: 'absolute', top: 0, left: 24, right: 24, height: '1px', background: 'linear-gradient(90deg,transparent,rgba(255,255,255,0.5),transparent)' } }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ pb: 1.5, mb: 1.5, borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: '50%', background: GRAD, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ForumIcon sx={{ color: '#fff', fontSize: 19 }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 600, color: '#fff', fontSize: '0.92rem', lineHeight: 1.1 }}>rootBot Assistant</Typography>
                    <Typography sx={{ color: '#34d399', fontSize: '0.72rem' }}>{tr(T.online)}</Typography>
                  </Box>
                </Stack>
                <Stack spacing={1.2}>
                  <Box sx={{ alignSelf: 'flex-start', maxWidth: '88%', background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.92)', px: 2, py: 1.15, borderRadius: '14px 14px 14px 4px', fontSize: '0.88rem' }}>{tr(T.chat1)}</Box>
                  <Box sx={{ alignSelf: 'flex-end', maxWidth: '88%', background: GRAD, color: '#fff', px: 2, py: 1.15, borderRadius: '14px 14px 4px 14px', fontSize: '0.88rem' }}>{tr(T.chat2)}</Box>
                  <Box sx={{ alignSelf: 'flex-start', maxWidth: '88%', background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.92)', px: 2, py: 1.15, borderRadius: '14px 14px 14px 4px', fontSize: '0.88rem' }}>{tr(T.chat3)}</Box>
                  <Box sx={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 0.6, background: 'rgba(255,255,255,0.1)', px: 1.8, py: 1.3, borderRadius: '14px 14px 14px 4px' }}>
                    {[0, 1, 2].map((i) => (<Box key={i} className="rb-dot" sx={{ width: 7, height: 7, borderRadius: '50%', background: 'rgba(255,255,255,0.85)' }} />))}
                  </Box>
                </Stack>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── TRUST STRIP ── */}
      <Box sx={{ background: '#fff', borderBottom: `1px solid ${LINE}`, py: { xs: 3.5, md: 4 } }}>
        <Container maxWidth="lg">
          <Typography sx={{ textAlign: 'center', color: MUTED, fontSize: '0.82rem', fontWeight: 600, letterSpacing: '0.08em', mb: 2.5 }}>{tr(T.trust)}</Typography>
          <Stack direction="row" spacing={{ xs: 1.5, md: 3 }} justifyContent="center" alignItems="center" flexWrap="wrap" useFlexGap>
            {TRUST.map((g) => (
              <Box key={g.name} sx={{ height: 54, px: 2.5, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: g.dark ? NAVY : '#fff', border: `1px solid ${g.dark ? NAVY : LINE}` }}>
                <Box component="img" src={g.logo} alt={g.name} sx={{ maxHeight: 30, maxWidth: 150, objectFit: 'contain', filter: g.dark ? 'none' : 'grayscale(15%)' }} />
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* ── 2. PROBLEM ── */}
      <Section bg="#fbfcff">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Eyebrow>{tr(SEC.problemEye)}</Eyebrow>
          <SectionTitle>{tr(SEC.problemTitle)}</SectionTitle>
        </Box>
        <Grid container spacing={3}>
          {PROBLEMS.map((p, i) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
              <SpotCard sx={{ ...card, p: 3.5 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '2.4rem', color: LINE, lineHeight: 1, mb: 1 }}>{`0${i + 1}`}</Typography>
                <Typography sx={{ fontWeight: 600, color: NAVY, mb: 1, fontSize: '1.08rem' }}>{tr(p.t)}</Typography>
                <Typography sx={{ color: MUTED, fontSize: '0.9rem', lineHeight: 1.65 }}>{tr(p.d)}</Typography>
              </SpotCard>
            </Grid>
          ))}
        </Grid>
      </Section>

      {/* ── 3. SOLUTION + KPI ── */}
      <Section>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Eyebrow>{tr(SEC.solutionEye)}</Eyebrow>
          <SectionTitle>{tr(SEC.solutionTitle)}</SectionTitle>
          <Lead>{tr(SEC.solutionLead)}</Lead>
        </Box>
        <Grid container spacing={2.5}>
          {KPIS.map((k) => (
            <Grid size={{ xs: 6, md: 3 }} key={k.l.en}>
              <SpotCard sx={{ ...card, p: 3.5, textAlign: 'center', background: POOL_TINT }}>
                <Typography sx={{ fontWeight: 700, fontSize: '2.1rem', mb: 0.5, background: GRAD, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', letterSpacing: '-0.02em' }}>{tr(k.v)}</Typography>
                <Typography sx={{ color: MUTED, fontSize: '0.84rem', lineHeight: 1.5 }}>{tr(k.l)}</Typography>
              </SpotCard>
            </Grid>
          ))}
        </Grid>
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Chip label={tr(SEC.crossTenant)} sx={{ fontWeight: 600, color: NAVY, background: '#eef2fb', border: `1px solid ${LINE}`, py: 2, px: 1 }} />
        </Box>
      </Section>

      {/* ── 4. PLATFORM OVERVIEW ── */}
      <Section bg="#fbfcff">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Eyebrow>{tr(SEC.platformEye)}</Eyebrow>
          <SectionTitle>{tr(SEC.platformTitle)}</SectionTitle>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0 }}>
          <Box sx={{ position: 'relative', textAlign: 'center', px: 5, py: 2.5, borderRadius: '16px', background: NAVY_GRAD, color: '#fff', boxShadow: '0 16px 40px -16px rgba(19,32,74,0.5)', maxWidth: 460 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '1.15rem' }}>rootBot Platform</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', mt: 0.3 }}>{tr(SEC.platformNodeSub)}</Typography>
          </Box>
        </Box>
        <Box sx={{ width: 2, height: 36, mx: 'auto', background: `linear-gradient(${SKY},${LINE})` }} />
        <Grid container spacing={3}>
          {TENANTS.map((t) => {
            const accent = t.mode === 'Silo' ? INDIGO : SKY;
            return (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={t.name}>
                <SpotCard sx={{ ...card, p: 3, borderTop: `3px solid ${accent}` }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                    <Chip label={t.mode} size="small" sx={{ fontWeight: 600, fontSize: '0.72rem', color: accent, background: t.mode === 'Silo' ? '#eceefe' : '#e6f6fe' }} />
                    {t.live && <Chip label="LIVE" size="small" sx={{ fontWeight: 700, fontSize: '0.66rem', color: '#fff', background: '#10b981' }} />}
                  </Stack>
                  <Typography sx={{ fontWeight: 600, color: NAVY, fontSize: '1.02rem' }}>{t.name}</Typography>
                  <Typography sx={{ color: accent, fontSize: '0.78rem', fontWeight: 500, mb: 1 }}>{t.ch}</Typography>
                  <Typography sx={{ color: MUTED, fontSize: '0.86rem', lineHeight: 1.6 }}>{tr(t.use)}</Typography>
                </SpotCard>
              </Grid>
            );
          })}
        </Grid>
      </Section>

      {/* ── 5. CAPABILITIES ── */}
      <Section sx={{ scrollMarginTop: '72px' }}>
        <Box id="capabilities" sx={{ textAlign: 'center', mb: 6 }}>
          <Eyebrow>{tr(SEC.capsEye)}</Eyebrow>
          <SectionTitle>{tr(SEC.capsTitle)}</SectionTitle>
        </Box>
        <Grid container spacing={3}>
          {CAPS.map((c) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={c.t.en}>
              <SpotCard sx={{ ...card, p: 3.5 }}>
                <Box sx={{ width: 50, height: 50, borderRadius: '14px', background: 'linear-gradient(135deg,rgba(14,165,233,0.12),rgba(99,102,241,0.12))', color: INDIGO, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5, '& svg': { fontSize: 26 } }}>
                  {c.icon}
                </Box>
                <Typography sx={{ fontWeight: 600, color: NAVY, mb: 0.9, fontSize: '1.06rem' }}>{tr(c.t)}</Typography>
                <Typography sx={{ color: MUTED, fontSize: '0.88rem', lineHeight: 1.65 }}>{tr(c.d)}</Typography>
              </SpotCard>
            </Grid>
          ))}
        </Grid>
      </Section>

      {/* ── 6. HOW IT WORKS ── */}
      <Section bg="#fbfcff">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Eyebrow>{tr(SEC.howEye)}</Eyebrow>
          <SectionTitle>{tr(SEC.howTitle)}</SectionTitle>
        </Box>
        <Grid container spacing={3}>
          {HOW.map((h, i) => (
            <Grid size={{ xs: 12, md: 4 }} key={h.t.en}>
              <SpotCard sx={{ ...card, p: 4 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '2.6rem', lineHeight: 1, mb: 1.5, background: GRAD, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{`0${i + 1}`}</Typography>
                <Typography sx={{ fontWeight: 600, color: NAVY, mb: 1.2, fontSize: '1.12rem' }}>{tr(h.t)}</Typography>
                <Typography sx={{ color: MUTED, fontSize: '0.9rem', lineHeight: 1.7 }}>{tr(h.d)}</Typography>
              </SpotCard>
            </Grid>
          ))}
        </Grid>
      </Section>

      {/* ── 7. POOL vs SILO ── */}
      <Section>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Eyebrow>{tr(SEC.deployEye)}</Eyebrow>
          <SectionTitle>{tr(SEC.deployTitle)}</SectionTitle>
          <Lead>{tr(SEC.deployLead)}</Lead>
        </Box>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[
            { key: 'Pool', tag: tr(SEC.poolTag), icon: <GroupsIcon />, accent: SKY, tint: POOL_TINT, data: POOL },
            { key: 'Silo', tag: tr(SEC.siloTag), icon: <DnsIcon />, accent: INDIGO, tint: SILO_TINT, data: SILO },
          ].map((p) => (
            <Grid size={{ xs: 12, md: 6 }} key={p.key}>
              <SpotCard sx={{ ...card, p: { xs: 3, md: 4 }, background: p.tint, border: `1px solid ${p.accent}22` }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5 }}>
                  <Box sx={{ width: 46, height: 46, borderRadius: '12px', background: p.accent, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{p.icon}</Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '1.4rem', color: NAVY, lineHeight: 1 }}>{p.key}</Typography>
                    <Typography sx={{ color: p.accent, fontSize: '0.82rem', fontWeight: 600 }}>{p.tag}</Typography>
                  </Box>
                </Stack>
                <Stack spacing={1.5}>
                  {p.data.bullets.map(([b, d]) => (
                    <Stack key={b.en} direction="row" spacing={1.3} alignItems="flex-start">
                      <CheckCircleIcon sx={{ color: p.accent, fontSize: 19, mt: 0.2 }} />
                      <Typography sx={{ fontSize: '0.9rem', lineHeight: 1.5 }}>
                        <Box component="span" sx={{ fontWeight: 600, color: NAVY }}>{tr(b)}</Box>
                        <Box component="span" sx={{ color: MUTED }}>{` — ${tr(d)}`}</Box>
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
                <Box sx={{ mt: 2.5, pt: 2, borderTop: `1px dashed ${p.accent}44` }}>
                  <Typography sx={{ fontSize: '0.84rem', color: MUTED }}>
                    <Box component="span" sx={{ fontWeight: 600, color: p.accent }}>{tr(SEC.fitLabel)}</Box>{tr(p.data.fit)}
                  </Typography>
                </Box>
              </SpotCard>
            </Grid>
          ))}
        </Grid>

        <Card sx={{ ...card, overflow: 'hidden', boxShadow: 'none' }}>
          <Grid container sx={{ background: NAVY_GRAD, color: '#fff' }}>
            <Grid size={4} sx={{ p: 1.8 }}><Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{L === 'th' ? 'เปรียบเทียบ' : 'Compare'}</Typography></Grid>
            <Grid size={4} sx={{ p: 1.8, borderLeft: '1px solid rgba(255,255,255,0.14)' }}><Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#7dd3fc' }}>Pool</Typography></Grid>
            <Grid size={4} sx={{ p: 1.8, borderLeft: '1px solid rgba(255,255,255,0.14)' }}><Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#c7d2fe' }}>Silo</Typography></Grid>
          </Grid>
          {COMPARE.map((row, i) => (
            <Grid container key={row[0].en} sx={{ background: i % 2 ? '#f8fafd' : '#fff', borderTop: `1px solid ${LINE}` }}>
              <Grid size={4} sx={{ p: 1.8 }}><Typography sx={{ fontWeight: 600, color: NAVY, fontSize: '0.83rem' }}>{tr(row[0])}</Typography></Grid>
              <Grid size={4} sx={{ p: 1.8, borderLeft: `1px solid ${LINE}` }}><Typography sx={{ color: MUTED, fontSize: '0.83rem', lineHeight: 1.5 }}>{tr(row[1])}</Typography></Grid>
              <Grid size={4} sx={{ p: 1.8, borderLeft: `1px solid ${LINE}` }}><Typography sx={{ color: MUTED, fontSize: '0.83rem', lineHeight: 1.5 }}>{tr(row[2])}</Typography></Grid>
            </Grid>
          ))}
        </Card>
        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center" sx={{ mt: 3.5 }} flexWrap="wrap" useFlexGap>
          <Chip label="Pool" size="small" sx={{ fontWeight: 700, color: '#fff', background: SKY }} />
          <ArrowForwardIcon sx={{ color: MUTED, fontSize: 18 }} />
          <Chip label="Silo" size="small" sx={{ fontWeight: 700, color: '#fff', background: INDIGO }} />
          <Typography sx={{ color: NAVY, fontWeight: 500, fontSize: '0.92rem' }}>{tr(SEC.migrate)}</Typography>
        </Stack>
      </Section>

      {/* ── 7b. ROI ── */}
      <Section bg="#fbfcff">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Eyebrow>{tr(SEC.roiEye)}</Eyebrow>
          <SectionTitle>{tr(SEC.roiTitle)}</SectionTitle>
        </Box>
        <Grid container spacing={3}>
          {ROI.map((r) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={r.t.en}>
              <SpotCard sx={{ ...card, p: 3.5, textAlign: 'center' }}>
                <Box sx={{ width: 56, height: 56, mx: 'auto', mb: 2, borderRadius: '16px', background: GRAD, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', '& svg': { fontSize: 28 } }}>{r.icon}</Box>
                <Typography sx={{ fontWeight: 600, color: NAVY, mb: 0.8, fontSize: '1.05rem' }}>{tr(r.t)}</Typography>
                <Typography sx={{ color: MUTED, fontSize: '0.88rem', lineHeight: 1.6 }}>{tr(r.d)}</Typography>
              </SpotCard>
            </Grid>
          ))}
        </Grid>
      </Section>

      {/* ── 8. ARCHITECTURE + SECURITY + MODEL (dark) ── */}
      <Box sx={{ position: 'relative', background: NAVY_GRAD, color: '#fff', overflow: 'hidden', py: { xs: 8, md: 13 } }}>
        <Glow color={INDIGO} top="-10%" left="-8%" size={560} opacity={0.34} />
        <Glow color={SKY} top="50%" right="-10%" size={520} opacity={0.28} />
        <GridTexture opacity={0.06} />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Eyebrow light>{tr(SEC.archEye)}</Eyebrow>
            <SectionTitle light>{tr(SEC.archTitle)}</SectionTitle>
          </Box>
          <Grid container spacing={2.5} sx={{ mb: 6 }}>
            {LAYERS.map((l, i) => (
              <Grid size={{ xs: 6, md: 3 }} key={l.t.en}>
                <Card sx={{ p: 3, height: '100%', borderRadius: '16px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', boxShadow: 'none' }}>
                  <Box sx={{ color: '#7dd3fc', mb: 1.5, '& svg': { fontSize: 28 } }}>{l.icon}</Box>
                  <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.08em' }}>{`${tr(SEC.layerWord)} ${i + 1}`}</Typography>
                  <Typography sx={{ fontWeight: 600, fontSize: '1.05rem', mb: 0.5 }}>{tr(l.t)}</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.68)', fontSize: '0.82rem', lineHeight: 1.5 }}>{tr(l.d)}</Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 7 }}>
              <Typography sx={{ fontWeight: 600, mb: 2.5, fontSize: '1.15rem' }}>{tr(SEC.securityTitle)}</Typography>
              <Grid container spacing={1.8}>
                {SECURITY.map((s) => (
                  <Grid size={{ xs: 12, sm: 6 }} key={s.en}>
                    <Stack direction="row" spacing={1.2} alignItems="flex-start">
                      <CheckCircleIcon sx={{ color: '#34d399', fontSize: 20, mt: 0.2, flexShrink: 0 }} />
                      <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.88rem', lineHeight: 1.5 }}>{tr(s)}</Typography>
                    </Stack>
                  </Grid>
                ))}
              </Grid>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <Typography sx={{ fontWeight: 600, mb: 2.5, fontSize: '1.15rem' }}>{tr(SEC.modelTitle)}</Typography>
              <Card sx={{ p: 2.5, mb: 2, borderRadius: '14px', background: 'rgba(14,165,233,0.12)', border: '1px solid rgba(14,165,233,0.32)', boxShadow: 'none' }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.6 }}>
                  <CloudIcon sx={{ color: '#7dd3fc', fontSize: 20 }} />
                  <Typography sx={{ fontWeight: 600 }}>Frontier LLM · Cloud</Typography>
                </Stack>
                <Typography sx={{ color: 'rgba(255,255,255,0.72)', fontSize: '0.84rem', lineHeight: 1.55 }}>{tr(SEC.frontierDesc)}</Typography>
              </Card>
              <Card sx={{ p: 2.5, borderRadius: '14px', background: 'rgba(99,102,241,0.14)', border: '1px solid rgba(99,102,241,0.35)', boxShadow: 'none' }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.6 }}>
                  <LanIcon sx={{ color: '#c7d2fe', fontSize: 20 }} />
                  <Typography sx={{ fontWeight: 600 }}>Local / Self-hosted LLM</Typography>
                </Stack>
                <Typography sx={{ color: 'rgba(255,255,255,0.72)', fontSize: '0.84rem', lineHeight: 1.55 }}>{tr(SEC.localDesc)}</Typography>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── 9. ONBOARDING ── */}
      <Section>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Eyebrow>{tr(SEC.onboardEye)}</Eyebrow>
          <SectionTitle>{tr(SEC.onboardTitle)}</SectionTitle>
        </Box>
        <Grid container spacing={3}>
          {STEPS.map((s, i) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={s.t.en}>
              <SpotCard sx={{ ...card, p: 3.5, height: '100%' }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                  <Box sx={{ width: 42, height: 42, borderRadius: '50%', background: GRAD, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '1.1rem', flexShrink: 0 }}>{i + 1}</Box>
                  {i < STEPS.length - 1 && <Box sx={{ flex: 1, height: 2, background: LINE, display: { xs: 'none', md: 'block' } }} />}
                </Stack>
                <Typography sx={{ fontWeight: 600, color: NAVY, mb: 0.9, fontSize: '1.05rem' }}>{tr(s.t)}</Typography>
                <Typography sx={{ color: MUTED, fontSize: '0.88rem', lineHeight: 1.6 }}>{tr(s.d)}</Typography>
              </SpotCard>
            </Grid>
          ))}
        </Grid>
      </Section>

      {/* ── 10. CUSTOMERS ── */}
      <Section bg="#fbfcff">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Eyebrow>{tr(SEC.custEye)}</Eyebrow>
          <SectionTitle>{tr(SEC.custTitle)}</SectionTitle>
        </Box>
        <Grid container spacing={3}>
          {CUSTOMERS.map((c) => (
            <Grid size={{ xs: 12, md: 6 }} key={c.name}>
              <SpotCard sx={{ ...card, p: { xs: 3, md: 4 }, borderTop: `3px solid ${c.accent}` }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                  <Box sx={{ height: 56, px: 2, borderRadius: '12px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: c.darkTile ? NAVY : '#fff', border: `1px solid ${c.darkTile ? NAVY : LINE}` }}>
                    <Box component="img" src={c.logo} alt={c.name} sx={{ maxHeight: 32, maxWidth: 170, objectFit: 'contain' }} />
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {c.live && <Chip label="LIVE" size="small" sx={{ fontWeight: 700, fontSize: '0.62rem', height: 20, color: '#fff', background: '#10b981' }} />}
                    <Chip label={c.mode} size="small" sx={{ fontWeight: 600, fontSize: '0.68rem', height: 20, color: c.accent, background: c.mode === 'Silo' ? '#eceefe' : '#e6f6fe' }} />
                  </Stack>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                  {c.mascot && (
                    <Box sx={{ width: 46, height: 46, borderRadius: '50%', overflow: 'hidden', border: `2px solid ${c.accent}40`, flexShrink: 0, background: '#f1f5fb' }}>
                      <Box component="img" src={c.mascot} alt={c.mascotName ? tr(c.mascotName) : ''} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </Box>
                  )}
                  <Box>
                    <Typography sx={{ fontWeight: 600, color: NAVY, fontSize: '1.12rem', lineHeight: 1.2 }}>{c.name}</Typography>
                    <Typography sx={{ color: MUTED, fontSize: '0.82rem' }}>{tr(c.sub)}</Typography>
                    {c.mascotName && <Typography sx={{ color: c.accent, fontSize: '0.74rem', fontWeight: 600, mt: 0.2 }}>{tr(SEC.botLabel)} “{tr(c.mascotName)}”</Typography>}
                  </Box>
                </Stack>
                <Box sx={{ height: '1px', background: LINE, mb: 2 }} />
                <Stack spacing={1}>
                  {c.points.map((p) => (
                    <Stack key={p.en} direction="row" spacing={1.2} alignItems="flex-start">
                      <CheckCircleIcon sx={{ color: c.accent, fontSize: 18, mt: 0.2, flexShrink: 0 }} />
                      <Typography sx={{ color: MUTED, fontSize: '0.86rem', lineHeight: 1.5 }}>{tr(p)}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </SpotCard>
            </Grid>
          ))}
        </Grid>
      </Section>

      {/* ── STATS BAND ── */}
      <Box sx={{ position: 'relative', background: NAVY_GRAD, color: '#fff', overflow: 'hidden', py: { xs: 6, md: 8 } }}>
        <Glow color={SKY} top="-40%" left="50%" size={560} opacity={0.3} />
        <GridTexture opacity={0.06} />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={3}>
            {STATS.map((s) => (
              <Grid size={{ xs: 6, md: 3 }} key={s.label.en} sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontWeight: 700, fontSize: { xs: '2.2rem', md: '3rem' }, lineHeight: 1, letterSpacing: '-0.02em', background: 'linear-gradient(135deg,#7dd3fc,#c7d2fe)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  {s.text ? s.text : <CountUp end={s.n as number} suffix={s.suffix} />}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: { xs: '0.78rem', md: '0.88rem' }, mt: 1 }}>{tr(s.label)}</Typography>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── 11. WHY ── */}
      <Section>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Eyebrow>{tr(SEC.whyEye)}</Eyebrow>
          <SectionTitle>{tr(SEC.whyTitle)}</SectionTitle>
        </Box>
        <Grid container spacing={2.5}>
          {WHY.map((w) => (
            <Grid size={{ xs: 12, md: 6 }} key={w.en}>
              <SpotCard sx={{ ...card, p: 3, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <Box sx={{ width: 30, height: 30, borderRadius: '8px', background: 'linear-gradient(135deg,rgba(14,165,233,0.14),rgba(99,102,241,0.14))', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <CheckCircleIcon sx={{ color: INDIGO, fontSize: 18 }} />
                </Box>
                <Typography sx={{ color: NAVY, fontSize: '0.95rem', lineHeight: 1.6 }}>{tr(w)}</Typography>
              </SpotCard>
            </Grid>
          ))}
        </Grid>
      </Section>

      {/* ── 12. CTA ── */}
      <Box sx={{ position: 'relative', background: GRAD, color: '#fff', overflow: 'hidden', py: { xs: 9, md: 14 }, textAlign: 'center' }}>
        <Glow color={SKY} top="-30%" left="50%" size={680} opacity={0.4} />
        <GridTexture opacity={0.07} />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: { xs: '2rem', md: '2.8rem' }, lineHeight: 1.2, letterSpacing: '-0.02em', textWrap: 'balance', mb: 2.5 }}>
            {SEC.ctaTitle[L][0]}<br />{SEC.ctaTitle[L][1]}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.92)', fontSize: { xs: '1rem', md: '1.12rem' }, mb: 1, lineHeight: 1.65 }}>{tr(SEC.ctaSub)}</Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.9rem', mb: 4.5 }}>{tr(SEC.ctaNote)}</Typography>
          <Button onClick={goDemo} size="large" endIcon={<ArrowForwardIcon />} sx={{ background: '#fff', color: INDIGO, px: 5, py: 1.7, fontWeight: 700, borderRadius: '12px', fontSize: '1.02rem', boxShadow: '0 16px 40px -12px rgba(0,0,0,0.5)', '&:hover': { background: '#eef2ff' } }}>
            {tr(SEC.ctaBtn)}
          </Button>
        </Container>
      </Box>

      {/* ── STICKY CTA BAR ── */}
      <Box sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1200, transform: showBar ? 'translateY(0)' : 'translateY(130%)', transition: 'transform .35s cubic-bezier(0.22,1,0.36,1)', background: 'rgba(10,16,36,0.92)', backdropFilter: 'blur(12px)', borderTop: '1px solid rgba(255,255,255,0.12)', py: 1.4 }}>
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
              <Box sx={{ width: 34, height: 34, borderRadius: '9px', background: GRAD, display: { xs: 'none', sm: 'flex' }, alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <ForumIcon sx={{ color: '#fff', fontSize: 18 }} />
              </Box>
              <Typography sx={{ color: '#fff', fontWeight: 500, fontSize: { xs: '0.85rem', md: '0.98rem' }, whiteSpace: { md: 'nowrap' }, overflow: 'hidden', textOverflow: 'ellipsis' }}>{tr(SEC.stickyMsg)}</Typography>
            </Stack>
            <Button onClick={goDemo} endIcon={<ArrowForwardIcon />} sx={{ background: GRAD, color: '#fff', px: { xs: 2.5, md: 3.5 }, py: 1.1, fontWeight: 600, borderRadius: '10px', flexShrink: 0, '&:hover': { opacity: 0.92 } }}>
              {tr(T.demo)}
            </Button>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default RootBotPage;
